import { CloudUploadOutlined, DeleteOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  Alert,
  App as AntApp,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';

import type { ProxyEntry, ProxySettings, ProxyStateResponse, ProxyType, RelayDeployInput, RelayPlatform, RotationMode } from '../../../src/types.ts';
import { api, ApiRequestError } from '../lib/api.ts';

interface Props {
  open: boolean;
  onClose: () => void;
}

const TYPE_COLORS: Record<ProxyType, string> = {
  cloudflare: 'orange',
  vercel: 'default',
  deno: 'green',
  http: 'blue',
};

const PLATFORM_FIELDS: Record<RelayPlatform, Array<{ name: keyof RelayDeployInput; label: string; secret?: boolean; hint?: string }>> = {
  cloudflare: [
    { name: 'accountId', label: 'Account ID', hint: 'Cloudflare dashboard → Workers & Pages → Account ID' },
    { name: 'apiToken', label: 'API token', secret: true, hint: 'Needs the "Edit Cloudflare Workers" template' },
  ],
  vercel: [{ name: 'vercelToken', label: 'API token', secret: true, hint: 'vercel.com/account/tokens' }],
  deno: [
    { name: 'denoToken', label: 'API token', secret: true, hint: 'dash.deno.com → Settings → Access Tokens' },
    { name: 'orgDomain', label: 'Organization domain', hint: 'e.g. myorg.deno.net' },
  ],
};

function statusTag(p: ProxyEntry) {
  if (p.testStatus === 'active') {
    return <Tag color="green">OK{p.lastLatencyMs != null ? ` · ${p.lastLatencyMs}ms` : ''}</Tag>;
  }
  if (p.testStatus === 'error') {
    return (
      <Tooltip title={p.lastError}>
        <Tag color="red">Failed</Tag>
      </Tooltip>
    );
  }
  return <Tag>Untested</Tag>;
}

/**
 * Manage the server's outbound relay pool.
 *
 * Only accounts listed in the server's ADMINS can open this — the pool is
 * server-wide, so editing it changes how every visitor's searches leave the box.
 */
export function SettingsDrawer({ open, onClose }: Props) {
  const { message } = AntApp.useApp();
  const [state, setState] = useState<ProxyStateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [platform, setPlatform] = useState<RelayPlatform>('cloudflare');
  const [addForm] = Form.useForm<{ url: string; name?: string; type: ProxyType }>();
  const [deployForm] = Form.useForm<RelayDeployInput>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setState(await api.proxies());
    } catch (err) {
      message.error(err instanceof ApiRequestError ? err.message : String(err), 6);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const onAdd = async (v: { url: string; name?: string; type: ProxyType }) => {
    try {
      await api.addProxy({ url: v.url.trim(), name: v.name?.trim(), type: v.type });
      addForm.resetFields();
      message.success('Relay added. Test it to confirm it can reach etravel.');
      void load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err), 6);
    }
  };

  const onTest = async (p: ProxyEntry) => {
    setTesting(p.id);
    try {
      const { result } = await api.testProxy(p.id);
      if (result.ok) message.success(`${p.name} reached etravel in ${result.latencyMs}ms.`);
      else message.error(`${p.name}: ${result.error ?? 'test failed'}`, 8);
      void load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err), 6);
    } finally {
      setTesting(null);
    }
  };

  const onToggle = async (p: ProxyEntry, isActive: boolean) => {
    try {
      await api.updateProxy(p.id, { isActive });
      void load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err), 6);
    }
  };

  const onDelete = async (p: ProxyEntry) => {
    try {
      await api.deleteProxy(p.id);
      message.success(`Removed ${p.name}.`);
      void load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err), 6);
    }
  };

  const onDeploy = async (v: RelayDeployInput) => {
    setDeploying(true);
    try {
      const { proxy } = await api.deployRelay(platform, v);
      deployForm.resetFields();
      message.success(`Deployed ${proxy.name} → ${proxy.url}`, 8);
      void load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err), 10);
    } finally {
      setDeploying(false);
    }
  };

  const onSetting = async (patch: Partial<ProxySettings>) => {
    try {
      await api.proxySettings(patch);
      void load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err), 6);
    }
  };

  const proxies = state?.proxies ?? [];
  const activeCount = proxies.filter((p) => p.isActive).length;
  const limits = state?.limits;
  const perRequest = state?.settings.rotation === 'request';

  return (
    <Drawer title="Settings" open={open} onClose={onClose} width={720} destroyOnHidden>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Outbound relay pool
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        etravel&apos;s firewall rate-limits per source IP, and every search leaves this server from one IP. A relay is a tiny worker on
        Cloudflare, Vercel or Deno that re-issues our requests from its own address, so the pool spreads searches across several IPs.
      </Typography.Paragraph>

      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Pool">
          <Space>
            <Switch
              checked={state?.settings.mode === 'pool'}
              onChange={(on) => void onSetting({ mode: on ? 'pool' : 'off' })}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
            <Typography.Text type="secondary">
              {activeCount} active of {proxies.length}
              {state?.settings.mode === 'off' ? ' · using the server’s own IP' : ''}
            </Typography.Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="On relay failure">
          <Space>
            <Switch
              checked={state?.settings.strict === true}
              onChange={(strict) => void onSetting({ strict })}
              checkedChildren="Fail"
              unCheckedChildren="Fall back"
            />
            <Typography.Text type="secondary">
              {state?.settings.strict ? 'A broken relay fails the search.' : 'A broken relay retries from this server directly.'}
            </Typography.Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Rotation">
          <Space direction="vertical" size={4}>
            <Segmented
              value={state?.settings.rotation ?? 'job'}
              onChange={(v) => void onSetting({ rotation: v as RotationMode })}
              options={[
                { value: 'job', label: 'Per search' },
                { value: 'request', label: 'Per request' },
              ]}
            />
            <Typography.Text type="secondary">
              {state?.settings.rotation === 'request'
                ? 'A different relay on every call — the widest IP spread, but etravel’s session cookie then arrives from a new IP each time, which can draw more challenges.'
                : 'One relay for a whole search, rotating between searches. Keeps etravel’s session cookie on a stable IP.'}
            </Typography.Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Requests per relay">
          <Space direction="vertical" size={4}>
            <Space>
              <InputNumber
                min={1}
                max={10}
                disabled={!perRequest}
                value={state?.settings.perRelayConcurrency ?? 2}
                onChange={(v) => {
                  if (typeof v === 'number') void onSetting({ perRelayConcurrency: v });
                }}
                style={{ width: 80 }}
              />
              {limits && (
                <Typography.Text type="secondary">
                  {limits.scaled
                    ? `× ${limits.relays} active ${limits.relays === 1 ? 'relay' : 'relays'} = ${limits.global} in flight · ${limits.perAccount} per account · ${limits.daysAtOnce} days at once`
                    : `not applied — ${limits.global} in flight · ${limits.perAccount} per account · ${limits.daysAtOnce} days at once`}
                </Typography.Text>
              )}
            </Space>
            <Typography.Text type="secondary">
              {perRequest
                ? 'etravel rate-limits per source IP, so this is what any one relay endures. The server-wide budget follows the pool automatically as relays come and go.'
                : 'Only applies to per-request rotation. Under per-search rotation a whole search rides one relay, so raising this would pile the entire load onto a single IP.'}
            </Typography.Text>
          </Space>
        </Descriptions.Item>
      </Descriptions>

      <Table<ProxyEntry>
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={proxies}
        pagination={false}
        locale={{ emptyText: 'No relays yet — add one below, or run scripts/import-9router-proxies.ts to bring them over from 9router.' }}
        columns={[
          {
            title: 'Relay',
            dataIndex: 'name',
            render: (name: string, p) => (
              <Space direction="vertical" size={0}>
                <Space size={4}>
                  <Typography.Text strong>{name}</Typography.Text>
                  <Tag color={TYPE_COLORS[p.type]}>{p.type}</Tag>
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }} copyable={{ text: p.url }}>
                  {p.url}
                </Typography.Text>
              </Space>
            ),
          },
          { title: 'Status', dataIndex: 'testStatus', width: 120, render: (_: unknown, p) => statusTag(p) },
          {
            title: 'Active',
            dataIndex: 'isActive',
            width: 80,
            render: (isActive: boolean, p) => <Switch size="small" checked={isActive} onChange={(on) => void onToggle(p, on)} />,
          },
          {
            title: '',
            width: 130,
            render: (_: unknown, p) => (
              <Space size={4}>
                <Button size="small" icon={<ThunderboltOutlined />} loading={testing === p.id} onClick={() => void onTest(p)}>
                  Test
                </Button>
                <Popconfirm title={`Remove ${p.name}?`} onConfirm={() => void onDelete(p)} okText="Remove" okButtonProps={{ danger: true }}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        Add an existing relay
      </Typography.Title>
      <Form form={addForm} layout="inline" initialValues={{ type: 'cloudflare' }} onFinish={(v) => void onAdd(v)}>
        <Form.Item name="url" rules={[{ required: true, message: 'Relay URL' }]} style={{ flex: 1, minWidth: 260 }}>
          <Input placeholder="https://my-relay.example.workers.dev" />
        </Form.Item>
        <Form.Item name="type">
          <Select
            style={{ width: 130 }}
            options={[
              { value: 'cloudflare', label: 'Cloudflare' },
              { value: 'vercel', label: 'Vercel' },
              { value: 'deno', label: 'Deno' },
              { value: 'http', label: 'HTTP proxy' },
            ]}
          />
        </Form.Item>
        <Form.Item name="name">
          <Input placeholder="Name (optional)" style={{ width: 150 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
            Add
          </Button>
        </Form.Item>
      </Form>

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        Deploy a new relay
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Your platform token is used for this deployment only and is never stored — only the resulting URL is kept."
      />
      <Segmented
        value={platform}
        onChange={(v) => {
          setPlatform(v as RelayPlatform);
          deployForm.resetFields();
        }}
        options={[
          { value: 'cloudflare', label: 'Cloudflare' },
          { value: 'vercel', label: 'Vercel' },
          { value: 'deno', label: 'Deno Deploy' },
        ]}
        style={{ marginBottom: 12 }}
      />
      <Form form={deployForm} layout="vertical" onFinish={(v) => void onDeploy(v)} requiredMark={false}>
        {PLATFORM_FIELDS[platform].map((f) => (
          <Form.Item key={String(f.name)} name={f.name} label={f.label} extra={f.hint} rules={[{ required: true, message: f.label }]}>
            {f.secret ? <Input.Password autoComplete="off" /> : <Input autoComplete="off" />}
          </Form.Item>
        ))}
        <Form.Item name="projectName" label="Project name" extra="Optional — a unique name is generated if you leave this blank.">
          <Input placeholder="etravel-relay" autoComplete="off" />
        </Form.Item>
        <Button type="primary" htmlType="submit" icon={<CloudUploadOutlined />} loading={deploying}>
          Deploy relay
        </Button>
      </Form>
    </Drawer>
  );
}
